// src/components/ProductDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProductDetail } from '../store/actions/productActions';
import TrialModal from './TrialModal'; // 导入独立的 TrialModal 组件
import LoginModal from './LoginModal'; // 导入 LoginModal 组件
import styles from '../styles/ProductDetail.module.css';

// 占位 UI 组件
const ProductDetailPlaceholder = () => (
  <section className={styles.productDetail}>
    <div className={styles.container}>
      <div className={styles.productContainer}>
        <div className={styles.productGallery}>
          <div className={styles.productImage}>
            <div className={styles.placeholderImage}></div>
          </div>
          {/* <div className={styles.thumbnailSlider}>
            {[...Array(4)].map((_, index) => (
              <div key={index} className={styles.thumbnail}>
                <div className={styles.placeholderThumbnail}></div>
              </div>
            ))}
          </div> */}
        </div>
        <div className={styles.productInfo}>
          <div className={styles.placeholderTitle}></div>
          <div className={styles.placeholderTypeSelector}>
            {[...Array(3)].map((_, index) => (
              <div key={index} className={styles.placeholderTypeOption}></div>
            ))}
          </div>
          <div className={styles.placeholderPrice}></div>
          <div className={styles.placeholderDescription}></div>
          <div className={styles.placeholderQuantity}>
            <div className={styles.placeholderQuantityLabel}></div>
            <div className={styles.placeholderQuantityInput}>
              {[...Array(3)].map((_, index) => (
                <div key={index} className={styles.placeholderQuantityBtn}></div>
              ))}
            </div>
          </div>
          <div className={styles.placeholderActions}>
            {[...Array(2)].map((_, index) => (
              <div key={index} className={styles.placeholderButton}></div>
            ))}
          </div>
          <div className={styles.placeholderGuarantee}>
            {[...Array(3)].map((_, index) => (
              <div key={index} className={styles.placeholderGuaranteeItem}>
                <div className={styles.placeholderGuaranteeIcon}></div>
                <div className={styles.placeholderGuaranteeText}></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// 映射 key 到天数
const typeDurationMap = {
  month: '30天',
  quarter: '90天',
  year: '365天',
};

// 映射 category 到 key
const categoryToKeyMap = {
  '30': 'month',
  '90': 'quarter',
  '365': 'year',
};

// 映射 type 到主图图片路径
const typeToImageMap = {
  gpt: `${process.env.PUBLIC_URL}/images/gpt-main.jpg`,
  vip: `${process.env.PUBLIC_URL}/images/vip-main.jpg`,
  grok: `${process.env.PUBLIC_URL}/images/grok-main.jpg`,
  claude: `${process.env.PUBLIC_URL}/images/claude-main.jpg`,
  mj: `${process.env.PUBLIC_URL}/images/mj-main.jpg`,
  default: `${process.env.PUBLIC_URL}/images/default-main.jpg`, // 默认图片
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { loading, productDetail, error } = useSelector((state) => state.products);
  const userData = useSelector((state) => state.user); // 获取完整的 user 数据
  const user = userData?.user; // 从 userData 中提取 user 对象
  const is_trial_gpt = user ? user.is_trial_gpt ?? 1 : 1; // 默认值 1（仅用于日志）

  const queryParams = new URLSearchParams(location.search);
  const initialType = queryParams.get('type') || ''; // 从 URL 获取 type 参数

  const [quantity, setQuantity] = useState(1);
  const [selectedType, setSelectedType] = useState('month'); // 默认类型
  // 注释掉与免费试用相关的状态
  // const [isTrialModalOpen, setIsTrialModalOpen] = useState(false); // 控制试用弹窗
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false); // 控制登录弹窗
  // const [pendingTrial, setPendingTrial] = useState(false); // 用于标记登录后是否需要打开试用弹窗

  // 打印 userData 和 is_trial_gpt 的初始值
  useEffect(() => {
    console.log('User data (state.user):', userData);
    console.log('User object:', user);
    console.log('is_trial_gpt value:', is_trial_gpt);
  }, [userData]);

  // 每次 id 或 initialType 变化时调用接口
  useEffect(() => {
    if (id && !loading) {
      console.log('调用 fetchProductDetail，id:', id, 'type:', initialType);
      dispatch(fetchProductDetail(id, { type: initialType }));
    }
  }, [dispatch, id, initialType]);

  // 设置默认选中类型
  useEffect(() => {
    if (productDetail && productDetail.types) {
      const sortedTypes = [...productDetail.types].sort((a, b) => a.price - b.price);
      const validKeys = sortedTypes.map((t) => t.key);

      let defaultTypeKey = sortedTypes[0]?.key || 'month';
      if (initialType && validKeys.includes(initialType)) {
        defaultTypeKey = initialType;
      } else {
        const category = productDetail.category;
        const defaultKeyFromCategory = categoryToKeyMap[category];
        if (defaultKeyFromCategory && validKeys.includes(defaultKeyFromCategory)) {
          defaultTypeKey = defaultKeyFromCategory;
        }
      }
      setSelectedType(defaultTypeKey);
    }
  }, [productDetail, initialType]);

  // 注释掉与免费试用相关的 useEffect
  // 登录成功后检查是否需要打开试用弹窗
  // useEffect(() => {
  //   if (user && pendingTrial) {
  //     console.log('用户登录成功且有待处理的试用请求，打开试用弹窗');
  //     setIsTrialModalOpen(true);
  //     setPendingTrial(false); // 重置标记
  //   }
  // }, [user, pendingTrial]);

  if (loading) {
    return <ProductDetailPlaceholder />;
  }

  if (error) {
    return (
      <section className={styles.productDetail}>
        <div className={styles.container}>
          <p>加载失败: {error}</p>
        </div>
      </section>
    );
  }

  if (!productDetail) {
    return (
      <section className={styles.productDetail}>
        <div className={styles.container}>
          <p>暂无产品详情</p>
        </div>
      </section>
    );
  }

  const sortedTypes = [...(productDetail.types || [])].sort((a, b) => a.price - b.price);
  const selectedTypeData = sortedTypes.find((t) => t.key === selectedType) || sortedTypes[0];

  const handleQuantityChange = (delta) => {
    setQuantity((prev) => Math.max(1, prev + delta));
  };

  // 注释掉与免费试用相关的函数
  // const handleFreeTrial = () => {
  //   console.log('点击免费试用，当前 user:', user, 'is_trial_gpt:', is_trial_gpt);
  //   if (!user) {
  //     console.log('用户未登录，先弹出登录窗口');
  //     setIsLoginModalOpen(true);
  //     setPendingTrial(true); // 设置标记，表示登录后需要打开试用弹窗
  //   } else {
  //     console.log('用户已登录，直接打开试用弹窗');
  //     setIsTrialModalOpen(true);
  //   }
  // };

  const handleBuyNow = () => {
    console.log('点击立即购买，当前 user:', user);
    if (!user) {
      console.log('用户未登录，弹出登录窗口');
      setIsLoginModalOpen(true); // 如果未登录，弹出登录窗口
    } else {
      console.log('用户已登录，进入支付流程');
      navigate(`/order?productId=${selectedTypeData.product_id}`, {
        state: {
          product: {
            ...productDetail,
            price: selectedTypeData?.price?.toFixed(2) || '0.00',
            quantity,
            selectedType,
          },
        },
      });
    }
  };

  // 注释掉与免费试用相关的函数
  // const handleTrialConfirm = () => {
  //   console.log('用户选择了试用，当前 user:', user, 'is_trial_gpt:', is_trial_gpt);
  //   setIsTrialModalOpen(false);
  // };

  // const handleTrialCancel = () => {
  //   console.log('用户取消试用，当前 user:', user, 'is_trial_gpt:', is_trial_gpt);
  //   setIsTrialModalOpen(false);
  // };

  const handleLoginClose = () => {
    console.log('关闭登录窗口');
    setIsLoginModalOpen(false);
    // setPendingTrial(false); // 如果用户取消登录，重置试用标记
  };

  const handleLoginSuccess = () => {
    console.log('用户登录成功，当前 user:', user);
    setIsLoginModalOpen(false); // 关闭登录弹窗
    // 不在这里直接打开试用弹窗，交给 useEffect 处理
  };

  const oldPriceNumber = selectedTypeData?.oldPrice ? Number(selectedTypeData.oldPrice) : null;

  // 获取真实的用户 ID 和产品类型
  const userKey = user?.id || user?.user_key || 9913; // 从 user 中获取 ID，fallback 到默认值
  const trialType = productDetail.type || 'grok'; // 使用产品详情中的 type，fallback 到 'grok'

  return (
    <section className={styles.productDetail}>
      <div className={styles.container}>
        <div className={styles.productContainer}>
          <div className={styles.productGallery}>
            <div className={styles.productImage}>
              {/* 根据 type 动态加载主图图片 */}
              <img
                src={typeToImageMap[productDetail.type] || typeToImageMap.default}
                alt={`${productDetail.name} 主图`}
                className={styles.mainImage}
              />
            </div>
            {/* <div className={styles.thumbnailSlider}>
              {(productDetail.thumbnails || ['fas fa-microchip', 'fas fa-robot', 'fas fa-brain', 'fas fa-laptop-code']).map((icon, index) => (
                <div key={index} className={`${styles.thumbnail} ${index === 0 ? styles.active : ''}`}>
                  <i className={icon}></i>
                </div>
              ))}
            </div> */}
          </div>
          <div className={styles.productInfo}>
            <h1>{productDetail.name || '未知产品'}</h1>
            <div className={styles.productTypeSelector}>
              {sortedTypes.map((type) => (
                <div
                  key={type.key}
                  className={`${styles.typeOption} ${selectedType === type.key ? styles.active : ''}`}
                  onClick={() => setSelectedType(type.key)}
                >
                  {typeDurationMap[type.key]}
                </div>
              ))}
            </div>
            <div className={styles.productPrice}>
              ￥{selectedTypeData?.price?.toFixed(2) || '0.00'}
              {/* {oldPriceNumber !== null && (
                <span className={styles.productOldPrice}>￥{oldPriceNumber.toFixed(2)}</span>
              )} */}
              {/* {oldPriceNumber !== null && (
                <span className={styles.productDiscount}>
                  {Math.round(((oldPriceNumber - selectedTypeData.price) / oldPriceNumber) * 100)}% 优惠
                </span>
              )} */}
            </div>
            <div className={styles.productDescription}>{productDetail.description || '暂无描述'}</div>
            <div className={styles.productQuantity}>
              <span className={styles.quantityLabel}>数量:</span>
              <div className={styles.quantityInput}>
                {/* <button className={styles.quantityBtn} onClick={() => handleQuantityChange(-1)}>-</button> */}
                <button className={styles.quantityBtn} onClick={() => handleQuantityChange(0)}>-</button>

                <input type="text" className={styles.quantityNum} value={quantity} readOnly />
                {/* <button className={styles.quantityBtn} onClick={() => handleQuantityChange(1)}>+</button> */}
                <button className={styles.quantityBtn} onClick={() => handleQuantityChange(0)}>+</button>

              </div>
              <span className={styles.quantityLimit}>（每次限购一个）</span>
            </div>
            <div className={styles.productActions}>
              {/* 注释掉免费试用按钮 */}
              {/* <button className={`${styles.btn} ${styles.freeTrial}`} onClick={handleFreeTrial}>
                免费试用
              </button> */}
              <button className={`${styles.btn} ${styles.buyNow}`} onClick={handleBuyNow}>
                立即购买
              </button>
            </div>
            <div className={styles.productGuarantee}>
              <div className={styles.guaranteeItem}>
                <i className="fas fa-shield-alt"></i>
                <span>账户安全保障</span>
              </div>
              <div className={styles.guaranteeItem}>
                <i className="fas fa-sync-alt"></i>
                <span>7天售后服务</span>
              </div>
              <div className={styles.guaranteeItem}>
                <i className="fas fa-headset"></i>
                <span>24/7技术支持</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* 注释掉试用弹窗 */}
      {/* <TrialModal
        isOpen={isTrialModalOpen}
        onClose={handleTrialCancel}
        userKey={userKey}
        type={trialType}
      /> */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={handleLoginClose}
        onLoginSuccess={handleLoginSuccess}
      />
    </section>
  );
};

export default React.memo(ProductDetail);